import {
  type Resource,
  RESOURCES,
  USER_ACTIONS,
  USER_ROLES,
  type UserAction,
} from "#customTypes/composedTypes/authTypes"
import { EnumValues } from "#customTypes/types"

// Role hierarchy - higher numbers inherit from lower numbers
const ROLE_HIERARCHY = {
  [USER_ROLES.DISABLED]: 0,
  [USER_ROLES.ASSISTANT]: 1,
  [USER_ROLES.TEACHER]: 2,
  [USER_ROLES.LEAD_TEACHER]: 3,
  [USER_ROLES.FRONT_DESK]: 4,
  [USER_ROLES.MANAGER]: 5,
  [USER_ROLES.SUPER]: 6,
  [USER_ROLES.DEV]: 7,
} as const

// Permission matrix: [role][resource][action] = boolean
type PermissionMatrix = {
  [K in EnumValues<typeof USER_ROLES>]?: {
    [R in Resource]?: {
      [A in UserAction]?: boolean
    }
  }
}

const PERMISSIONS: PermissionMatrix = {
  [USER_ROLES.DISABLED]: {
    // Disabled users cannot do anything, including login
  },

  [USER_ROLES.ASSISTANT]: {
    [RESOURCES.USER]: {
      [USER_ACTIONS.LOGIN]: true,
    },
    [RESOURCES.EVALUATION]: {
      [USER_ACTIONS.READ]: true,
      [USER_ACTIONS.UPDATE]: true,
    },
    [RESOURCES.TEACHER_VIEW]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.EVALUATIONS_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.TEACHER_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.PROFILE_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
    // Quality Check is look-only and open to every role that can log in
    // (JARVIS-128); lead teachers are the primary persona.
    [RESOURCES.QUALITY_CHECK_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
  },

  // Teachers inherit all Assistant permissions
  [USER_ROLES.TEACHER]: {
    // Teachers browse the Standout Review stream and thumbs-up colleagues'
    // moments (positive loop). The thumbs-down / share / edit-others actions
    // are the LEAD_TEACHER UPDATE grant below.
    [RESOURCES.STANDOUT_REVIEW_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.HELP_REQUEST]: {
      [USER_ACTIONS.CREATE]: true,
      [USER_ACTIONS.READ]: true,
      [USER_ACTIONS.UPDATE]: true,
    },
    [RESOURCES.STUDENT]: {
      [USER_ACTIONS.READ]: true,
      [USER_ACTIONS.UPDATE]: true,
    },
    [RESOURCES.CLASS]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.CURRICULUM_PLAN]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.PROJECT]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.SEMESTER]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.TAG]: {
      [USER_ACTIONS.READ]: true,
      [USER_ACTIONS.UPDATE]: true,
    },
    [RESOURCES.EVALUATION]: {
      [USER_ACTIONS.READ]: true,
      [USER_ACTIONS.UPDATE]: true,
    },
    [RESOURCES.TEACHER_VIEW]: {
      [USER_ACTIONS.READ]: true,
    },
  },

  // Lead Teachers inherit all Teacher permissions
  [USER_ROLES.LEAD_TEACHER]: {
    [RESOURCES.STUDENT]: {
      [USER_ACTIONS.CREATE]: true,
      [USER_ACTIONS.UPDATE]: true,
      [USER_ACTIONS.VIEW_CONTACT_INFO]: true,
      [USER_ACTIONS.UPLOAD_DOCUMENT]: true,
      // Hide/restore artwork (nondestructive delete) — deliberately NOT
      // granted to TEACHER, so it can't ride along with STUDENT UPDATE.
      [USER_ACTIONS.HIDE_ARTWORK]: true,
    },
    [RESOURCES.CLASS]: {
      [USER_ACTIONS.CREATE]: true,
      [USER_ACTIONS.UPDATE]: true,
    },
    [RESOURCES.CURRICULUM_PLAN]: {
      [USER_ACTIONS.CREATE]: true,
      [USER_ACTIONS.UPDATE]: true,
    },
    [RESOURCES.PROJECT]: {
      [USER_ACTIONS.CREATE]: true,
      [USER_ACTIONS.UPDATE]: true,
    },
    [RESOURCES.SEMESTER]: {
      [USER_ACTIONS.CREATE]: true,
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.MAKEUP]: {
      [USER_ACTIONS.READ]: true,
      [USER_ACTIONS.CREATE]: true,
      [USER_ACTIONS.UPDATE]: true,
      [USER_ACTIONS.DELETE]: true,
    },
    [RESOURCES.TRYOUT]: {
      [USER_ACTIONS.READ]: true,
      [USER_ACTIONS.CREATE]: true,
      [USER_ACTIONS.UPDATE]: true,
      [USER_ACTIONS.DELETE]: true,
    },
    [RESOURCES.TAG]: {
      [USER_ACTIONS.CREATE]: true,
      [USER_ACTIONS.UPDATE]: true,
    },
    [RESOURCES.EVALUATION]: {
      [USER_ACTIONS.CREATE]: true,
      [USER_ACTIONS.READ]: true,
      [USER_ACTIONS.UPDATE]: true,
      [USER_ACTIONS.DELETE]: true,
      [USER_ACTIONS.ASSIGN_TEACHER_TO_EVAL]: true,
    },
    [RESOURCES.STUDENTS_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.CLASSES_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.MAKEUPS_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.TRYOUTS_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
    // UPDATE gates the Standout Review moderation actions: thumbs-down,
    // share-with-parents, and editing other teachers' moments. Thumbs-down
    // marks are visible only to this grant plus the moment's own author.
    [RESOURCES.STANDOUT_REVIEW_PAGE]: {
      [USER_ACTIONS.UPDATE]: true,
    },
  },

  // Front Desk inherit all Lead Teacher permissions
  [USER_ROLES.FRONT_DESK]: {
    [RESOURCES.HELP_REQUEST]: {
      [USER_ACTIONS.DELETE]: true,
    },
    [RESOURCES.STUDENT]: {
      [USER_ACTIONS.VIEW_CONTACT_INFO]: true,
      [USER_ACTIONS.UPDATE]: true,
      [USER_ACTIONS.DELETE]: true,
    },
    [RESOURCES.CLASS]: {
      [USER_ACTIONS.DELETE]: true,
    },
    [RESOURCES.CURRICULUM_PLAN]: {
      [USER_ACTIONS.DELETE]: true,
    },
    [RESOURCES.PROJECT]: {
      [USER_ACTIONS.DELETE]: true,
    },
    [RESOURCES.SEMESTER]: {
      [USER_ACTIONS.UPDATE]: true,
      [USER_ACTIONS.DELETE]: true,
    },
    [RESOURCES.TAG]: {
      [USER_ACTIONS.DELETE]: true,
    },
    [RESOURCES.USER]: {
      [USER_ACTIONS.CREATE]: true,
      [USER_ACTIONS.READ]: true,
      [USER_ACTIONS.UPDATE]: true,
      [USER_ACTIONS.VIEW_CONTACT_INFO]: true,
    },
    [RESOURCES.FRONT_DESK_VIEW]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.PROJECTS_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.CURRICULUM_PLANS_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.USERS_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.TAGS_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.SEMESTERS_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.FRONT_DESK_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
  },

  // Managers inherit all Front Desk permissions
  [USER_ROLES.MANAGER]: {
    [RESOURCES.USER]: {
      [USER_ACTIONS.DELETE]: true,
    },
    // Longer-term notes about staff members (dashboard popover + user
    // profile section).
    [RESOURCES.STAFF_NOTE]: {
      [USER_ACTIONS.CREATE]: true,
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.SETTINGS]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.SETTINGS_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.LOGIN]: {
      [USER_ACTIONS.MANAGER_LOGOUT]: true,
    },
  },

  // Super users inherit all Manager permissions
  [USER_ROLES.SUPER]: {
    [RESOURCES.USER]: {
      [USER_ACTIONS.CREATE_SUPER_USER]: true,
      [USER_ACTIONS.DELETE_SUPER_USER]: true,
    },
    [RESOURCES.TEST]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.TEST_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.AUTH_TEST_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
  },

  [USER_ROLES.DEV]: {
    [RESOURCES.ID]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.TEST_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
    [RESOURCES.AUTH_TEST_PAGE]: {
      [USER_ACTIONS.READ]: true,
    },
  },
}

const hasPermissionForRole = (
  role: EnumValues<typeof USER_ROLES>,
  resource: Resource,
  action: UserAction
): boolean => {
  const rolePermissions = PERMISSIONS[role]
  if (!rolePermissions) return false

  const resourcePermissions = rolePermissions[resource]
  if (!resourcePermissions) return false

  return resourcePermissions[action] === true
}

const hasInheritedPermission = (
  userRole: EnumValues<typeof USER_ROLES>,
  resource: Resource,
  action: UserAction
): boolean => {
  const userRoleLevel = ROLE_HIERARCHY[userRole]

  // Check permissions for this role and all roles it inherits from
  for (const [role, level] of Object.entries(ROLE_HIERARCHY)) {
    if (level <= userRoleLevel) {
      if (
        hasPermissionForRole(
          role as EnumValues<typeof USER_ROLES>,
          resource,
          action
        )
      ) {
        return true
      }
    }
  }

  return false
}

export const canAssignRole = (
  actorRole: EnumValues<typeof USER_ROLES>,
  targetRole: EnumValues<typeof USER_ROLES>
): boolean => {
  return ROLE_HIERARCHY[actorRole] >= ROLE_HIERARCHY[targetRole]
}

// Main permission checker function
export const hasPermission = (
  userRole: EnumValues<typeof USER_ROLES>,
  resource: Resource,
  action: UserAction
): boolean => {
  // Disabled users have no permissions
  if (userRole === USER_ROLES.DISABLED) {
    return false
  }

  return hasInheritedPermission(userRole, resource, action)
}

// Export the permission matrix and hierarchy for advanced use cases
export { PERMISSIONS, ROLE_HIERARCHY }
